import { useEffect, useMemo, useState } from "react";
import TranslationReader from "./TranslationReader";
import SpotifyCTA from "./SpotifyCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 12 languages planned for the global edition. English is live now; the
// others appear as a "coming soon" badge until each text + audio edition
// is published.
export const LANGUAGES = [
  { code: "en", flag: "🇬🇧", name: "English",  native: "English" },
  { code: "de", flag: "🇩🇪", name: "German",   native: "Deutsch" },
  { code: "fr", flag: "🇫🇷", name: "French",   native: "Français" },
  { code: "es", flag: "🇪🇸", name: "Spanish",  native: "Español" },
  { code: "it", flag: "🇮🇹", name: "Italian",  native: "Italiano" },
  { code: "el", flag: "🇬🇷", name: "Greek",    native: "Ελληνικά" },
  { code: "tr", flag: "🇹🇷", name: "Turkish",  native: "Türkçe" },
  { code: "ar", flag: "🇸🇦", name: "Arabic",   native: "العربية" },
  { code: "nl", flag: "🇳🇱", name: "Dutch",    native: "Nederlands" },
  { code: "ru", flag: "🇷🇺", name: "Russian",  native: "Русский" },
  { code: "zh", flag: "🇨🇳", name: "Chinese",  native: "中文" },
  { code: "hi", flag: "🇮🇳", name: "Hindi",    native: "हिन्दी" },
];

const AVAILABLE_FALLBACK = new Set(["en", "tr"]);

export function Dedication() {
  return (
    <section className="dedication" data-testid="dedication">
      <div className="dedication-rule" aria-hidden="true" />
      <p className="dedication-kicker">Dedicated to</p>
      <p className="dedication-body">
        my late wife <strong>Sally</strong>,
        <br />
        my children,
        <br />
        and my close friends.
      </p>
      <div className="dedication-rule" aria-hidden="true" />
    </section>
  );
}

export function LanguagePicker() {
  const [open, setOpen] = useState(false);
  const [readerLang, setReaderLang] = useState(null);
  const [availSet, setAvailSet] = useState(AVAILABLE_FALLBACK);
  const [audioMap, setAudioMap] = useState({});  // code -> count
  const [highlight, setHighlight] = useState(LANGUAGES[0]);  // active Spotify CTA target
  const current = LANGUAGES[0];

  useEffect(() => {
    fetch(`${API}/book/translations`)
      .then((r) => r.json())
      .then((d) => {
        const set = new Set(["en"]);
        const audio = {};
        for (const t of d.translations || []) {
          if (t.chapter_count > 0) set.add(t.code);
          audio[t.code] = t.audio_count || 0;
        }
        setAvailSet(set);
        setAudioMap(audio);
      })
      .catch(() => {});
  }, []);

  // Listen for cross-component requests to open a specific language reader
  // (fired by the auto-detect language router banner at the top of the page).
  useEffect(() => {
    const onOpen = (e) => {
      const code = e?.detail?.code;
      if (!code) return;
      const lang = LANGUAGES.find((l) => l.code === code);
      if (!lang) return;
      setHighlight(lang);
      if (code !== "en") setReaderLang(lang);
    };
    window.addEventListener("tb:open-lang", onOpen);
    return () => window.removeEventListener("tb:open-lang", onOpen);
  }, []);

  const items = useMemo(() => LANGUAGES.map((l) => ({
    ...l,
    available: availSet.has(l.code),
    audioCount: audioMap[l.code] || 0,
  })), [availSet, audioMap]);

  const onSelect = (lang) => {
    setOpen(false);
    setHighlight(lang);  // always update the Spotify CTA target
    if (!lang.available) {
      window.alert(
        `${lang.name} (${lang.native}) edition is coming soon — sign up via ` +
        "the comments below if you'd like to be notified when it launches."
      );
      return;
    }
    if (lang.code === "en") return;  // English is the default view
    setReaderLang(lang);
  };

  return (
    <section className="lang" id="lang" data-testid="lang">
      <div className="lang-head">
        <div className="kicker">Twelve languages · one global story</div>
        <h2 className="h2">
          Read & listen in <em>your language</em>
        </h2>
        <p className="lang-blurb">
          Twice Built, Twice Broken is being translated into 12 languages so
          its themes of love, loss, resilience and hope can cross borders.
          Each edition will be available as text on this site and chapter-by-chapter
          podcasts on Spotify and Apple Podcasts.
        </p>
      </div>

      <div className="lang-current">
        <button
          type="button"
          className="lang-current-btn"
          onClick={() => setOpen((o) => !o)}
          data-testid="lang-current-btn"
          aria-expanded={open}
        >
          <span className="lang-current-flag" aria-hidden="true">{current.flag}</span>
          <span className="lang-current-label">
            Currently reading in <strong>{current.native}</strong>
          </span>
          <span className="lang-current-caret" aria-hidden="true">{open ? "▲" : "▼"}</span>
        </button>
      </div>

      {open && (
        <ul className="lang-list" data-testid="lang-list">
          {items.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                className={"lang-chip " + (l.available ? "lang-chip-avail" : "lang-chip-soon")}
                onClick={() => onSelect(l)}
                data-testid={`lang-chip-${l.code}`}
              >
                <span className="lang-flag" aria-hidden="true">{l.flag}</span>
                <span className="lang-name">
                  <span className="lang-native">{l.native}</span>
                  <span className="lang-en">{l.name}</span>
                </span>
                <span className={"lang-status " + (l.available ? "ok" : "soon")}>
                  {l.available ? "Available" : "Coming soon"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="lang-spotify-anchor" data-testid="lang-spotify-anchor">
        <SpotifyCTA code={highlight.code} native={highlight.native} />
      </div>

      {readerLang && (
        <TranslationReader
          code={readerLang.code}
          name={readerLang.native}
          flag={readerLang.flag}
          onClose={() => setReaderLang(null)}
        />
      )}
    </section>
  );
}
